import { PresenceIndicator } from '../badges/PresenceIndicator';

import { DrawerBase } from './DrawerBase';

import type { AttendantVM } from '../../view-models';

export interface AttendantDrawerStubProps {
  open: boolean;
  attendant: AttendantVM | null;
  onClose: () => void;
  onMensagemPrivada?: () => void;
  onAcompanhar?: () => void;
  /**
   * Salto cross-tela p/ o Dashboard de Qualidade filtrado por este atendente
   * (Item 352). Opcional (espelha onAcompanhar/onMensagemPrivada): a PainelEquipePage
   * só passa o handler quando o caller tem `quality-aggregations-read` E a tela está
   * pronta — senão o botão fica desabilitado (honest-defer). Stub apresentacional puro.
   */
  onVerQualidade?: () => void;
}

export function AttendantDrawerStub({
  open,
  attendant,
  onClose,
  onMensagemPrivada,
  onAcompanhar,
  onVerQualidade,
}: AttendantDrawerStubProps): React.JSX.Element | null {
  if (attendant === null) return null;
  const total = attendant.modes.h_a + attendant.modes.h_b + attendant.modes.h_c;
  return (
    <DrawerBase
      open={open}
      onClose={onClose}
      title={`Perfil — ${attendant.name}`}
      footer={
        <div className="drawer__foot-actions">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={onMensagemPrivada === undefined}
            title={onMensagemPrivada === undefined ? 'Em breve' : undefined}
            onClick={onMensagemPrivada}
          >
            Mensagem privada
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={onAcompanhar === undefined}
            onClick={onAcompanhar}
          >
            Acompanhar
          </button>
        </div>
      }
    >
      <section className="att-drawer__section">
        <div className="att-drawer__meta">
          <PresenceIndicator
            presence={attendant.presence}
            {...(attendant.awayMinutes !== undefined ? { awayMinutes: attendant.awayMinutes } : {})}
          />
          <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 'var(--text-xs)' }}>
            · última ação há {attendant.lastAction}
          </span>
        </div>
        <div className="att-drawer__role">{attendant.role}</div>
        <div className="att-drawer__specs">{attendant.specialties.join(' · ')}</div>
      </section>

      <section className="att-drawer__section">
        <div className="att-drawer__section-title">Conversas ativas hoje</div>
        <div className="att-drawer__row">
          <span>Total</span>
          <b>{total}</b>
        </div>
        <div className="att-drawer__row">
          <span>Você dirige (H_a)</span>
          <b>{attendant.modes.h_a}</b>
        </div>
        <div className="att-drawer__row">
          <span>Você + IA (H_b)</span>
          <b>{attendant.modes.h_b}</b>
        </div>
        <div className="att-drawer__row">
          <span>IA dirige (H_c)</span>
          <b>{attendant.modes.h_c}</b>
        </div>
        <div className="att-drawer__row">
          <span>SLA atual</span>
          <b className={`sla sla--${attendant.slaStatus}`}>{attendant.sla}s</b>
        </div>
      </section>

      {attendant.behavioralPattern !== undefined && (
        <section className="att-drawer__section att-drawer__section--warn">
          <div className="att-drawer__section-title">Padrão comportamental detectado</div>
          <div className="att-drawer__alert">
            <b>{attendant.behavioralPattern.label}</b>
            <span className="mono" style={{ color: 'var(--fg-3)', fontSize: 'var(--text-xs)' }}>
              · severidade {attendant.behavioralPattern.severity}
            </span>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginTop: 8 }}
            disabled={onVerQualidade === undefined}
            title={
              onVerQualidade === undefined
                ? 'Disponível com acesso ao Dashboard de Qualidade'
                : undefined
            }
            onClick={onVerQualidade}
          >
            Ver no Dashboard de Qualidade →
          </button>
        </section>
      )}

      <section className="att-drawer__section">
        <div className="att-drawer__section-title">Drill-down</div>
        <div className="att-drawer__hint mono">Perfil completo em breve.</div>
      </section>
    </DrawerBase>
  );
}
